import {
  type ElementType,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useTable,
  useGlobalFilter,
  usePagination,
  useBlockLayout,
  useSortBy,
} from "react-table";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";

import NoDataFound from "../NoDataFound";
import TableGlobalFilter from "./TableGlobalFilter";
import TablePageSize from "./TablePageSize";
import TablePagination from "./TablePagination";
import DataRangeDisplay from "../DataRangeDisplay";
import RpTableSkeleton from "../muiCustom/skeleton/RpTableSkeleton";

type TableRecord = Record<string, any>;
type FetchDataParams = {
  filters: TableRecord;
  pageIndex: number;
  pageSize: number;
  searchKey?: string;
  sortBy: string;
  sortByDir: string;
};
type TableServerProps = {
  columns?: TableRecord[];
  containerClass?: string;
  data?: TableRecord[];
  emptyState?: ReactNode;
  fetchData?: (_params: FetchDataParams) => void;
  filters?: ((_setFilter: any, _filters: TableRecord) => ReactNode) | null;
  hideSearchLabel?: boolean;
  initialPage?: number;
  initialPageSize?: number;
  initialSortedBy?: TableRecord[];
  isLoading?: boolean;
  isSubscriptionHistory?: boolean;
  manualSortBy?: boolean;
  pageCount?: number;
  showPageSize?: boolean;
  showPagination?: boolean;
  showSearch?: boolean;
  showSkeletonLoader?: boolean;
  skeletonRowCount?: number;
  totalRecords?: number;
  useLayout?: boolean;
};

const TableGlobalFilterComponent = TableGlobalFilter as ElementType;
const TablePageSizeComponent = TablePageSize as ElementType;
const TablePaginationComponent = TablePagination as ElementType;
const DataRangeDisplayComponent = DataRangeDisplay as ElementType;
const RpTableSkeletonComponent = RpTableSkeleton as ElementType;

export default function TableServer({
  columns = [],
  data = [],
  initialPageSize = 10,
  pageCount,
  isLoading,
  filters = null,
  showPageSize = false,
  showPagination = true,
  fetchData,
  useLayout = false,
  initialSortedBy = [],
  hideSearchLabel = false,
  totalRecords,
  showSearch = false,
  isSubscriptionHistory = false,
  initialPage = 0,
  containerClass = "",
  emptyState = null,
  showSkeletonLoader = false,
  skeletonRowCount = 5,
  manualSortBy = true,
}: TableServerProps) {
  const [selectedFilters, setSelectedFilters] = useState<TableRecord>({});
  const previousInitialPageRef = useRef(initialPage);

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: initialPage,
        pageSize: initialPageSize,
        sortBy: initialSortedBy,
      },
      manualPagination: true,
      pageCount,
      manualGlobalFilter: true,
      manualSortBy,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useLayout && useBlockLayout
  );

  // Listen for changes in pagination and use the state to fetch our new data

  useEffect(() => {
    // reset page on change filter
    gotoPage(0);
  }, [pageSize, state.globalFilter, selectedFilters, gotoPage]);

  useEffect(() => {
    if (previousInitialPageRef.current !== initialPage) {
      previousInitialPageRef.current = initialPage;
    } else {
      return;
    }

    if (pageIndex !== initialPage) {
      gotoPage(initialPage);
    }
  }, [gotoPage, initialPage, pageIndex]);

  useEffect(() => {
    if (fetchData && typeof fetchData === "function") {
      let sortParams;
      let sortByDir;
      if (sortBy.length > 0) {
        sortParams = sortBy[0];
        sortByDir = sortParams.desc ? "desc" : "asc";
      }

      fetchData({
        pageIndex: pageIndex,
        pageSize,
        searchKey: state.globalFilter,
        filters: selectedFilters,
        sortBy: manualSortBy ? sortParams?.id || "" : "",
        sortByDir: manualSortBy ? sortByDir || "" : "",
      });
    }
  }, [
    pageIndex,
    pageSize,
    state.globalFilter,
    selectedFilters,
    fetchData,
    manualSortBy,
    sortBy,
  ]);

  const handlePageChange = (_event: unknown, value: number) => {
    gotoPage(value - 1);
  };

  const handleOnChangeFilter = (filterKey: string, filterValue: unknown) => {
    if (filterKey) {
      setSelectedFilters({ ...selectedFilters, [filterKey]: filterValue });
    }
  };

  const getHederTitle = (column: TableRecord) => {
    if (column.isSorted) {
      return column.isSortedDesc ? `Sorted DESC` : `Sorted ASC`;
    }

    return "";
  };

  const noOfSpan = useMemo(() => {
    const { values } = page;
    return Object.keys(values)?.length || 100;
  }, [page]);

  return (
    <div className="rp-react-table table-server">
      {showSearch && (
        <div
          className={`${
            isSubscriptionHistory
              ? "justify-content-between"
              : "justify-content-end"
          } row my-1 table-search align-items-center`}
        >
          {filters && filters(handleOnChangeFilter, selectedFilters)}
          <div
            className={`${filters ? "col col-lg-3 col-xl-3" : "col-md-4 "} ${
              isSubscriptionHistory ? "col col-lg-4 col-xl-4 mb-3" : ""
            } mb-3`}
          >
            <TableGlobalFilterComponent
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={state.globalFilter}
              setGlobalFilter={setGlobalFilter}
              placeholder="Search.."
              debounceTime={500}
              hideSearchLabel={hideSearchLabel}
            />
          </div>
        </div>
      )}

      <div className={`table-responsive ${containerClass}`}>
        <table {...getTableProps()} className="table table-borderless">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: hgKey, ...hgProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={hgKey} {...hgProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colProps } = column.getHeaderProps(
                      column.getSortByToggleProps({
                        title: getHederTitle(column),
                      })
                    );
                    return (
                      <th key={colKey} {...colProps}>
                        {column.render("Header")}
                        <div
                          style={{
                            position: "absolute",
                            right: 2,
                            top: 0,
                            bottom: 0,
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignContent: "center",
                          }}
                        >
                          {column.canSort ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignContent: "center",
                                height: "100%",
                              }}
                            >
                              <ArrowUpward
                                style={{ fontSize: 9 }}
                                color={
                                  column.isSorted && !column.isSortedDesc
                                    ? "inherit"
                                    : "disabled"
                                }
                              />
                              <ArrowDownward
                                style={{ fontSize: 9 }}
                                color={
                                  column.isSorted && column.isSortedDesc
                                    ? "inherit"
                                    : "disabled"
                                }
                              />
                            </div>
                          ) : null}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              showSkeletonLoader ? (
                <RpTableSkeletonComponent
                  columns={headerGroups[0]?.headers || []}
                  rowCount={skeletonRowCount}
                />
              ) : (
                <tr>
                  <td colSpan={noOfSpan}>
                    <NoDataFound
                      message="Loading..."
                      height={page?.length ? page?.length * 50 : "auto"}
                    />
                  </td>
                </tr>
              )
            ) : page && page.length ? (
              page.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={42}>
                  {emptyState || <NoDataFound height="auto" />}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-1">
        <div className="row align-items-center">
          {showPageSize === true && (
            <div className="col-1">
              <TablePageSizeComponent
                pageSize={pageSize}
                setPageSize={setPageSize}
              />
            </div>
          )}
          <div className="col-md-6 mb-1 ml-1">
            <DataRangeDisplayComponent
              pageIndex={pageIndex}
              totalRecords={totalRecords}
            />
          </div>
          {showPagination === true && pageCount > 0 && (
            <div className="col d-flex justify-content-end">
              <TablePaginationComponent
                count={pageCount}
                page={pageIndex + 1}
                onChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
